import { Injectable, inject } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { HttpClient, HttpParams } from '@angular/common/http';
import { environment } from '../../environments/environment';

// Report export trigger types
export interface ReportExportRequest {
  fromDate?: string;
  toDate?: string;
}

export interface DataExportRequest extends ReportExportRequest {
  countries?: string[];
  materialNumbers?: string[];
}

export interface ReportExportResult {
  success: boolean;
  s3Key?: string;
  bucketName?: string;
  fileName?: string;
  recordCount?: number;
  errorMessage?: string;
  downloadUrl?: string;
}

export interface ReportExportResponse {
  result: ReportExportResult;
  appliedConfig: {
    fromDate: string;
    toDate: string;
    loadingLocationGroupPrefix?: string;
    countries?: string[];
    materialNumbers?: string[];
  };
}

export interface ReportAccessConfig {
  chinaLoadingListUsers: string[];
  dataExportUsers: string[];
}

export interface SystemConfigurationItem {
  configType: string;
  itemId: string;
  itemValue?: string;
  name?: string;
  description?: string;
}

// Report types
export interface MaterialsReportItem {
  date: string;
  materialNumber: string;
  material: string;
  loadingFacilityName: string;
  sumWeight: number;
  weightUom: string;
  sumSU: number;
  carrierName: string;
  sourceTwo?: string;
}

export interface MaterialsReportTotals {
  weightUom: string;
  sumWeights: number;
  sumSU: number;
}

export interface MaterialsReportResponse {
  report: {
    items: MaterialsReportItem[];
    pagination: PaginationInfo;
  };
  totals: MaterialsReportTotals[];
  appliedFilters: any;
}

export interface EquipmentReportItem {
  loadingLocationName: string;
  carrierName: string;
  equipmentType: string;
  shippingUnitCount: number;
  totalWeight: number;
  weightUom: string;
}

export interface EquipmentReportResponse {
  report: {
    items: EquipmentReportItem[];
    pagination: PaginationInfo;
  };
  totals: {
    totalShippingUnits: number;
    byCarrier: Record<string, number>;
  };
  appliedFilters: any;
}

export interface DemurrageReportItem {
  id: string;
  loadingLocationName: string;
  licensePlate: string;
  carrierName: string;
  checkInTime: string;
  checkOutTime?: string;
  turnaroundMinutes: number;
  allowedMinutes: number;
  excessMinutes: number;
  demurrageAmount: number;
  demurrageCurrency: string;
  freightOrderId?: string;
}

export interface DemurrageReportResponse {
  report: {
    items: DemurrageReportItem[];
    pagination: PaginationInfo;
  };
  summary: {
    totalRecords: number;
    averageTurnaroundMinutes: number;
    totalDemurrageAmount: number;
    demurrageCurrency: string;
    byCarrier: Record<string, { avgTurnaround: number; totalDemurrage: number }>;
  };
  appliedFilters: any;
}

export interface VehicleReportItem {
  id: string;
  loadingLocationName: string;
  licensePlate: string;
  carrierName: string;
  driverName: string;
  checkInTime: string;
  checkOutTime?: string;
  status: string;
  freightOrderId?: string;
  checksGroup0: CheckAttributes;
  checksGroup1: CheckAttributes;
  checksGroup2: CheckAttributes;
  checksGroup3: CheckAttributes;
  checksGroup4: CheckAttributes;
  checksGroup5: CheckAttributes;
}

export interface CheckAttributes {
  passed: boolean;
  timestamp?: string;
  user?: string;
  notes?: string;
}

export interface VehicleReportResponse {
  report: {
    items: VehicleReportItem[];
    pagination: PaginationInfo;
  };
  summary: {
    totalVehicles: number;
    checkedIn: number;
    checkedOut: number;
    inProgress: number;
    byStatus: Record<string, number>;
    byCarrier: Record<string, number>;
  };
  appliedFilters: any;
}

export interface DefectDetail {
  id: string;
  defectId: string;
  suNumber: string;
  description?: string;
  createdBy: string;
  createdByName?: string;
  created: string;
  imageUrls?: string[];
}

export interface ContainerDefectsReportItem {
  id: string;
  checkNumber: string;
  checkLabel?: string;
  containerNumber: string;
  closed?: string;
  isResolved: boolean;
  details: DefectDetail[];
  firstReportedDate: string;
  lastUpdatedDate?: string;
  freightOrderId?: string;
  shippingUnitNumber?: string;
}

export interface ContainerDefectsReportResponse {
  report: {
    items: ContainerDefectsReportItem[];
    pagination: PaginationInfo;
  };
  summary: {
    totalDefects: number;
    openDefects: number;
    closedDefects: number;
    byCheckNumber: Record<string, number>;
  };
  appliedFilters: any;
}

export interface PaginationInfo {
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  nextCursor?: string;
  previousCursor?: string;
  pageSize: number;
  currentPageSize: number;
  totalCount: number;
}

// Filter types
export interface MaterialsReportFilter {
  fromDate: string;
  untilDate: string;
  loadingLocationGroupId?: string;
  loadingLocationId?: string;
  carrierId?: string;
  loadingFacilityId?: string;
  materialNumber?: string;
  shippingUnitNumber?: string;
}

export interface EquipmentReportFilter {
  year: number;
  month: number;
  loadingLocationGroupId?: string;
  loadingLocationId?: string;
  carrierId?: string;
}

export interface DemurrageReportFilter {
  loadingLocationGroupId: string;
  dateFrom?: string;
  dateUntil?: string;
  loadingLocationId?: string;
  carrierId?: string;
  licensePlate?: string;
}

export interface VehicleReportFilter {
  loadingLocationGroupId: string;
  dateFrom?: string;
  dateUntil?: string;
  loadingLocationId?: string;
  carrierId?: string;
  licensePlate?: string;
}

export interface ContainerDefectsReportFilter {
  dateFrom?: string;
  dateUntil?: string;
  containerNumber?: string;
  checkNumber?: string;
  email?: string;
  openOnly?: boolean;
  closedOnly?: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class ReportService {
  private readonly http = inject(HttpClient);
  private readonly apiUrl = environment.apiUrl;

  /**
   * Get Materials Report
   * NOTE: Backend API is not yet deployed - using mock data for development
   */
  getMaterialsReport(
    filter: MaterialsReportFilter,
    limit = 50,
    cursor?: string,
  ): Observable<MaterialsReportResponse> {
    // TODO: Remove this when backend is deployed
    // Return mock data directly since backend reports API is not deployed yet
    console.log('MaterialsReport: Using mock data (backend not deployed)', { filter, limit, cursor });
    return of(this.getMockMaterialsReport());

    // Uncomment below when backend is deployed:
    /*
    let params = new HttpParams()
      .set('fromDate', filter.fromDate)
      .set('untilDate', filter.untilDate)
      .set('limit', limit.toString());

    if (filter.loadingLocationGroupId) {
      params = params.set('loadingLocationGroupId', filter.loadingLocationGroupId);
    }
    if (filter.loadingLocationId) {
      params = params.set('loadingLocationId', filter.loadingLocationId);
    }
    if (filter.carrierId) {
      params = params.set('carrierId', filter.carrierId);
    }
    if (filter.loadingFacilityId) {
      params = params.set('loadingFacilityId', filter.loadingFacilityId);
    }
    if (filter.materialNumber) {
      params = params.set('materialNumber', filter.materialNumber);
    }
    if (filter.shippingUnitNumber) {
      params = params.set('shippingUnitNumber', filter.shippingUnitNumber);
    }
    if (cursor) {
      params = params.set('cursor', cursor);
    }

    return this.http
      .get<MaterialsReportResponse>(`${this.apiUrl}/reports/materials`, { params })
      .pipe(
        catchError((error) => {
          console.error('Error fetching materials report:', error);
          return of(this.getMockMaterialsReport());
        }),
      );
    */
  }

  /**
   * Get Equipment Report
   */
  getEquipmentReport(
    filter: EquipmentReportFilter,
    limit = 50,
    cursor?: string,
  ): Observable<EquipmentReportResponse> {
    let params = new HttpParams()
      .set('year', filter.year.toString())
      .set('month', filter.month.toString())
      .set('limit', limit.toString());

    if (filter.loadingLocationGroupId) {
      params = params.set('loadingLocationGroupId', filter.loadingLocationGroupId);
    }
    if (filter.loadingLocationId) {
      params = params.set('loadingLocationId', filter.loadingLocationId);
    }
    if (filter.carrierId) {
      params = params.set('carrierId', filter.carrierId);
    }
    if (cursor) {
      params = params.set('cursor', cursor);
    }

    return this.http
      .get<EquipmentReportResponse>(`${this.apiUrl}/reports/equipment`, { params })
      .pipe(
        catchError((error) => {
          console.error('Error fetching equipment report:', error);
          return of(this.getMockEquipmentReport());
        }),
      );
  }

  /**
   * Get Demurrage Report
   */
  getDemurrageReport(
    filter: DemurrageReportFilter,
    limit = 50,
    cursor?: string,
  ): Observable<DemurrageReportResponse> {
    let params = new HttpParams()
      .set('loadingLocationGroupId', filter.loadingLocationGroupId)
      .set('limit', limit.toString());

    if (filter.dateFrom) {
      params = params.set('dateFrom', filter.dateFrom);
    }
    if (filter.dateUntil) {
      params = params.set('dateUntil', filter.dateUntil);
    }
    if (filter.loadingLocationId) {
      params = params.set('loadingLocationId', filter.loadingLocationId);
    }
    if (filter.carrierId) {
      params = params.set('carrierId', filter.carrierId);
    }
    if (filter.licensePlate) {
      params = params.set('licensePlate', filter.licensePlate);
    }
    if (cursor) {
      params = params.set('cursor', cursor);
    }

    return this.http
      .get<DemurrageReportResponse>(`${this.apiUrl}/reports/demurrage`, { params })
      .pipe(
        catchError((error) => {
          console.error('Error fetching demurrage report:', error);
          return of(this.getMockDemurrageReport());
        }),
      );
  }

  /**
   * Get Vehicle Report
   */
  getVehicleReport(
    filter: VehicleReportFilter,
    limit = 50,
    cursor?: string,
  ): Observable<VehicleReportResponse> {
    let params = new HttpParams()
      .set('loadingLocationGroupId', filter.loadingLocationGroupId)
      .set('limit', limit.toString());

    if (filter.dateFrom) {
      params = params.set('dateFrom', filter.dateFrom);
    }
    if (filter.dateUntil) {
      params = params.set('dateUntil', filter.dateUntil);
    }
    if (filter.loadingLocationId) {
      params = params.set('loadingLocationId', filter.loadingLocationId);
    }
    if (filter.carrierId) {
      params = params.set('carrierId', filter.carrierId);
    }
    if (filter.licensePlate) {
      params = params.set('licensePlate', filter.licensePlate);
    }
    if (cursor) {
      params = params.set('cursor', cursor);
    }

    return this.http
      .get<VehicleReportResponse>(`${this.apiUrl}/reports/vehicle`, { params })
      .pipe(
        catchError((error) => {
          console.error('Error fetching vehicle report:', error);
          return of(this.getMockVehicleReport());
        }),
      );
  }

  /**
   * Get Container Defects Report
   */
  getContainerDefectsReport(
    filter: ContainerDefectsReportFilter,
    limit = 50,
    cursor?: string,
  ): Observable<ContainerDefectsReportResponse> {
    let params = new HttpParams().set('limit', limit.toString());

    if (filter.dateFrom) {
      params = params.set('dateFrom', filter.dateFrom);
    }
    if (filter.dateUntil) {
      params = params.set('dateUntil', filter.dateUntil);
    }
    if (filter.containerNumber) {
      params = params.set('containerNumber', filter.containerNumber);
    }
    if (filter.checkNumber) {
      params = params.set('checkNumber', filter.checkNumber);
    }
    if (filter.email) {
      params = params.set('email', filter.email);
    }
    if (filter.openOnly) {
      params = params.set('openOnly', 'true');
    }
    if (filter.closedOnly) {
      params = params.set('closedOnly', 'true');
    }
    if (cursor) {
      params = params.set('cursor', cursor);
    }

    return this.http
      .get<ContainerDefectsReportResponse>(`${this.apiUrl}/reports/container-defects`, {
        params,
      })
      .pipe(
        catchError((error) => {
          console.error('Error fetching container defects report:', error);
          return of(this.getMockContainerDefectsReport());
        }),
      );
  }

  // Mock data for development
  private getMockMaterialsReport(): MaterialsReportResponse {
    return {
      report: {
        items: [
          {
            date: new Date().toISOString().split('T')[0],
            materialNumber: 'MAT001',
            material: 'Sample Material A',
            loadingFacilityName: 'Dock 1',
            sumWeight: 1500.5,
            weightUom: 'KG',
            sumSU: 5,
            carrierName: 'Carrier 1',
            sourceTwo: 'REF-001',
          },
          {
            date: new Date().toISOString().split('T')[0],
            materialNumber: 'MAT002',
            material: 'Sample Material B',
            loadingFacilityName: 'Dock 2',
            sumWeight: 2.5,
            weightUom: 'TO',
            sumSU: 3,
            carrierName: 'Carrier 2',
            sourceTwo: 'REF-002',
          },
        ],
        pagination: {
          hasNextPage: false,
          hasPreviousPage: false,
          pageSize: 50,
          currentPageSize: 2,
          totalCount: 2,
        },
      },
      totals: [
        { weightUom: 'KG', sumWeights: 1500.5, sumSU: 5 },
        { weightUom: 'TO', sumWeights: 2.5, sumSU: 3 },
      ],
      appliedFilters: {},
    };
  }

  private getMockEquipmentReport(): EquipmentReportResponse {
    return {
      report: {
        items: [
          {
            loadingLocationName: 'Location A',
            carrierName: 'Carrier 1',
            equipmentType: 'Container 20ft',
            shippingUnitCount: 15,
            totalWeight: 12500,
            weightUom: 'KG',
          },
        ],
        pagination: {
          hasNextPage: false,
          hasPreviousPage: false,
          pageSize: 50,
          currentPageSize: 1,
          totalCount: 1,
        },
      },
      totals: {
        totalShippingUnits: 15,
        byCarrier: { 'Carrier 1': 15 },
      },
      appliedFilters: {},
    };
  }

  private getMockDemurrageReport(): DemurrageReportResponse {
    return {
      report: {
        items: [
          {
            id: 'dem-001',
            loadingLocationName: 'Location A',
            licensePlate: 'AB-123-CD',
            carrierName: 'Carrier 1',
            checkInTime: '2024-01-15T08:00:00Z',
            checkOutTime: '2024-01-15T10:30:00Z',
            turnaroundMinutes: 150,
            allowedMinutes: 120,
            excessMinutes: 30,
            demurrageAmount: 45.0,
            demurrageCurrency: 'EUR',
          },
        ],
        pagination: {
          hasNextPage: false,
          hasPreviousPage: false,
          pageSize: 50,
          currentPageSize: 1,
          totalCount: 1,
        },
      },
      summary: {
        totalRecords: 1,
        averageTurnaroundMinutes: 150,
        totalDemurrageAmount: 45.0,
        demurrageCurrency: 'EUR',
        byCarrier: {
          'Carrier 1': { avgTurnaround: 150, totalDemurrage: 45.0 },
        },
      },
      appliedFilters: {},
    };
  }

  private getMockVehicleReport(): VehicleReportResponse {
    return {
      report: {
        items: [
          {
            id: 'veh-001',
            loadingLocationName: 'Location A',
            licensePlate: 'AB-123-CD',
            carrierName: 'Carrier 1',
            driverName: 'John Doe',
            checkInTime: '2024-01-15T08:00:00Z',
            checkOutTime: '2024-01-15T10:30:00Z',
            status: 'checked_out',
            checksGroup0: { passed: true },
            checksGroup1: { passed: true },
            checksGroup2: { passed: true },
            checksGroup3: { passed: true },
            checksGroup4: { passed: true },
            checksGroup5: { passed: false },
          },
        ],
        pagination: {
          hasNextPage: false,
          hasPreviousPage: false,
          pageSize: 50,
          currentPageSize: 1,
          totalCount: 1,
        },
      },
      summary: {
        totalVehicles: 1,
        checkedIn: 0,
        checkedOut: 1,
        inProgress: 0,
        byStatus: { checked_out: 1 },
        byCarrier: { 'Carrier 1': 1 },
      },
      appliedFilters: {},
    };
  }

  private getMockContainerDefectsReport(): ContainerDefectsReportResponse {
    return {
      report: {
        items: [
          {
            id: 'def-001',
            checkNumber: 'check402',
            checkLabel: 'Container Interior',
            containerNumber: 'CONT123456',
            isResolved: false,
            details: [
              {
                id: 'det-001',
                defectId: 'def-001',
                suNumber: 'SU-001',
                description: 'Dent on interior wall',
                createdBy: 'CWID001',
                createdByName: 'John Doe',
                created: '2024-01-15T08:00:00Z',
              },
            ],
            firstReportedDate: '2024-01-15T08:00:00Z',
            lastUpdatedDate: '2024-01-15T10:30:00Z',
          },
        ],
        pagination: {
          hasNextPage: false,
          hasPreviousPage: false,
          pageSize: 50,
          currentPageSize: 1,
          totalCount: 1,
        },
      },
      summary: {
        totalDefects: 1,
        openDefects: 1,
        closedDefects: 0,
        byCheckNumber: { check402: 1 },
      },
      appliedFilters: {},
    };
  }

  // ==========================================================================
  // REPORT EXPORT TRIGGER METHODS
  // ==========================================================================

  /**
   * Trigger China Loading List export
   * Requires Admin, TechAdmin, or OperationalAdmin role
   */
  triggerChinaLoadingList(request?: ReportExportRequest): Observable<ReportExportResponse> {
    // Send null body if no request params - backend handles empty body gracefully
    const body = request && Object.keys(request).length > 0 ? request : null;
    return this.http
      .post<ReportExportResponse>(`${this.apiUrl}/reports/china-loading-list`, body)
      .pipe(
        catchError((error) => {
          console.error('Error triggering China Loading List export:', error);
          throw error;
        }),
      );
  }

  /**
   * Trigger Data Export
   * Requires Admin, TechAdmin, or OperationalAdmin role
   */
  triggerDataExport(request?: DataExportRequest): Observable<ReportExportResponse> {
    // Send null body if no request params - backend handles empty body gracefully
    const body = request && Object.keys(request).length > 0 ? request : null;
    return this.http
      .post<ReportExportResponse>(`${this.apiUrl}/reports/data-export`, body)
      .pipe(
        catchError((error) => {
          console.error('Error triggering Data Export:', error);
          throw error;
        }),
      );
  }

  /**
   * Get report access configuration from SystemConfiguration
   * Returns list of CWIDs authorized to trigger each report type
   */
  getReportAccessConfig(): Observable<ReportAccessConfig> {
    return this.http
      .get<SystemConfigurationItem[]>(
        `${this.apiUrl}/system-configurations/REPORT_ACCESS_SETTINGS`,
      )
      .pipe(
        map((items) => {
          const config: ReportAccessConfig = {
            chinaLoadingListUsers: [],
            dataExportUsers: [],
          };

          for (const item of items) {
            if (item.itemId === 'CHINA_LOADING_LIST_USERS' && item.itemValue) {
              config.chinaLoadingListUsers = item.itemValue
                .split(',')
                .map((s) => s.trim())
                .filter((s) => s.length > 0);
            }
            if (item.itemId === 'DATA_EXPORT_USERS' && item.itemValue) {
              config.dataExportUsers = item.itemValue
                .split(',')
                .map((s) => s.trim())
                .filter((s) => s.length > 0);
            }
          }

          return config;
        }),
        catchError((error) => {
          console.error('Error fetching report access config:', error);
          // Return empty config on error - falls back to role-based access only
          return of({
            chinaLoadingListUsers: [],
            dataExportUsers: [],
          });
        }),
      );
  }

  /**
   * Save report access configuration to SystemConfiguration
   * Updates both items individually via PUT /system-configurations/{configType}/{itemId}
   */
  saveReportAccessConfig(config: ReportAccessConfig): Observable<void> {
    const updateChinaList$ = this.http.put<SystemConfigurationItem>(
      `${this.apiUrl}/system-configurations/REPORT_ACCESS_SETTINGS/CHINA_LOADING_LIST_USERS`,
      { itemValue: config.chinaLoadingListUsers.join(',') },
    );

    const updateDataExport$ = this.http.put<SystemConfigurationItem>(
      `${this.apiUrl}/system-configurations/REPORT_ACCESS_SETTINGS/DATA_EXPORT_USERS`,
      { itemValue: config.dataExportUsers.join(',') },
    );

    return new Observable((subscriber) => {
      Promise.all([updateChinaList$.toPromise(), updateDataExport$.toPromise()])
        .then(() => {
          subscriber.next();
          subscriber.complete();
        })
        .catch((error) => {
          subscriber.error(error);
        });
    });
  }

  /**
   * Check if user can trigger China Loading List export
   * - Admin users: always allowed
   * - Other users: must be in the CHINA_LOADING_LIST_USERS list
   */
  canTriggerChinaLoadingList(
    userCwid: string,
    userRoles: string[],
    accessConfig: ReportAccessConfig,
  ): boolean {
    // Admin users always have access
    const isAdmin = userRoles.some((role) => role.toLowerCase() === 'admin');
    if (isAdmin) {
      return true;
    }

    // Other users must be in the allowed list (case-insensitive)
    return accessConfig.chinaLoadingListUsers.some(
      (cwid) => cwid.toLowerCase() === userCwid.toLowerCase(),
    );
  }

  /**
   * Check if user can trigger Data Export (Basic Chemical List)
   * - Admin users: always allowed
   * - Other users: must be in the DATA_EXPORT_USERS list
   */
  canTriggerDataExport(
    userCwid: string,
    userRoles: string[],
    accessConfig: ReportAccessConfig,
  ): boolean {
    // Admin users always have access
    const isAdmin = userRoles.some((role) => role.toLowerCase() === 'admin');
    if (isAdmin) {
      return true;
    }

    // Other users must be in the allowed list (case-insensitive)
    return accessConfig.dataExportUsers.some(
      (cwid) => cwid.toLowerCase() === userCwid.toLowerCase(),
    );
  }
}
